import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { getClassLabelFromAkeneoObject } from "../utils/akeneoUtils";
import { useUIStore } from "../stores/ui-store";
import { Company, InvoiceLanguageEnum } from "../api/models/companies.models";

interface CompanyInvoicingInformationProps {
  company?: Company;
}

const InvoicingInfoContainer = styled.div`
  margin-bottom: 2rem;
  margin-top: 0;
  padding: 0 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;

  & h3 {
    font-size: 1.2rem;
    font-weight: 600;
    margin-top: 0;
    margin-bottom: 1rem;
  }
`;

const InvoicingHeader = styled.span`
  font-weight: 500;
  font-size: 0.95rem;
`;

export const CompanyInvoicingInformation: React.FC<
  CompanyInvoicingInformationProps
> = ({ company }) => {
  const { t } = useTranslation();
  const locale = useUIStore((state) => state.locale);

  const businessIdentifierId = 50;
  const vatIdentifierId = 100;

  if (!company?.invoicingInformation) {
    return null;
  }

  const vatIdentifier = company?.identifiers.find(
    (id) => id.identifierType.id === vatIdentifierId,
  )?.text;

  const businessId = company.identifiers.find(
    (id) => id.identifierType.id === businessIdentifierId,
  )?.text;

  const getInvoiceLanguageTranslation = (languageCode: InvoiceLanguageEnum) => {
    switch (languageCode) {
      case InvoiceLanguageEnum.Finnish:
        return t("label-finnish");
      case InvoiceLanguageEnum.Swedish:
        return t("label-swedish");
      case InvoiceLanguageEnum.English:
        return t("label-english");
      default:
        return languageCode;
    }
  };

  return (
    <InvoicingInfoContainer>
      <h3 style={{ marginTop: 0, marginBottom: "1rem" }}>
        {t("label-invoicing-information")}
      </h3>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "0.5rem",
        }}
      >
        {(company?.invoicingInformation?.companyName ||
          company?.companyName) && (
          <div>
            <InvoicingHeader>{t("label-invoiced-company")}:</InvoicingHeader>{" "}
            {company.invoicingInformation.companyName || company.companyName}
          </div>
        )}

        {(company?.invoicingInformation?.businessIdentifier || businessId) && (
          <div>
            <InvoicingHeader>{t("label-business-id")}:</InvoicingHeader>{" "}
            {company?.invoicingInformation?.businessIdentifier || businessId}
          </div>
        )}

        <div>
          <InvoicingHeader>{t("label-ovt-number")}:</InvoicingHeader>{" "}
          {company.invoicingInformation.ovtNumber}
        </div>
        <div>
          <InvoicingHeader>{t("label-operator")}:</InvoicingHeader>{" "}
          {company.invoicingInformation.operator}
        </div>
        <div>
          <InvoicingHeader>{t("label-invoicing-email")}:</InvoicingHeader>{" "}
          {company.invoicingInformation.email}
        </div>
        <div>
          <InvoicingHeader>{t("label-invoice-reference")}:</InvoicingHeader>{" "}
          {company.invoicingInformation.invoiceReference}
        </div>
        <div>
          <InvoicingHeader>{t("label-invoicing-language")}:</InvoicingHeader>{" "}
          {getInvoiceLanguageTranslation(
            company.invoicingInformation.invoiceLanguage,
          )}
        </div>
        {vatIdentifier && (
          <div>
            <InvoicingHeader>{t("label-vat-number")}:</InvoicingHeader>{" "}
            {vatIdentifier}
          </div>
        )}

        {company.invoicingInformation.address && (
          <div style={{ gridColumn: "1 / -1" }}>
            <InvoicingHeader>
              {t("label-invoicing-company-address")}:
            </InvoicingHeader>{" "}
            {company.invoicingInformation.address.addressLine}
            {company.invoicingInformation.address.addressLine2 &&
              `, ${company.invoicingInformation.address.addressLine2}`}
            {", "}
            {company.invoicingInformation.address.postCode}{" "}
            {company.invoicingInformation.address.city}
            {", "}
            {company.invoicingInformation.address.country &&
              getClassLabelFromAkeneoObject(
                locale,
                company.invoicingInformation.address.country,
              )}
          </div>
        )}
      </div>
    </InvoicingInfoContainer>
  );
};
